import React from 'react';
import { Check } from 'lucide-react';
import { moduleApi } from '../../lib/api';

/**
 * En-tête de la page d'un dossier (refonte Dossiers clients, docs/REFONTE-DOSSIERS-CLIENTS.md) : une frise en quatre
 * étapes (Demande, Devis & accord, Passages, Facturation) et une carte « Prochaine action » avec UN bouton principal.
 * Il se lit en dix secondes et dit où en est le dossier. Il ne remplace pas encore le panneau de l'étape qui est
 * dessous : les boutons agissent par les mêmes fonctions que ce panneau (ou y font défiler la page).
 */

type Etat = 'fait' | 'en_cours' | 'a_venir';
type Etape = { cle: string; titre: string; detail: string; etat: Etat };

const jour = (iso?: string | null) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }); } catch { return ''; }
};

/** Quatre étapes de la frise, déduites de l'étape (stage) du dossier et de quelques dates. Fonction pure. */
export function etapesFrise(d: any, passages?: { fait: number; total: number } | null): Etape[] {
    const s = String(d?.stage || '');
    const ordre = ['demande_recue', 'dossier_cree', 'devis_envoye', 'devis_signe', 'rdv_reserve', 'prestation_realisee', 'cloturee'];
    const rang = ordre.indexOf(s);
    const avantDevisSigne = rang < ordre.indexOf('devis_signe');

    const demande: Etape = rang === 0
        ? { cle: 'demande', titre: 'Demande', detail: 'À étudier', etat: 'en_cours' }
        : { cle: 'demande', titre: 'Demande', detail: jour(d?.created_at) || 'Reçue', etat: 'fait' };

    const devis: Etape = !avantDevisSigne
        ? { cle: 'devis', titre: 'Devis & accord', detail: d?.devis_signed_at ? `Accepté le ${jour(d.devis_signed_at)}` : 'Accepté', etat: 'fait' }
        : s === 'devis_envoye'
            ? { cle: 'devis', titre: 'Devis & accord', detail: d?.devis_sent_at ? `Envoyé le ${jour(d.devis_sent_at)}` : 'En attente', etat: 'en_cours' }
            : s === 'dossier_cree'
                ? { cle: 'devis', titre: 'Devis & accord', detail: 'À établir', etat: 'en_cours' }
                : { cle: 'devis', titre: 'Devis & accord', detail: '—', etat: 'a_venir' };

    const nbPassages = passages && passages.total > 1 ? ` · ${passages.fait} sur ${passages.total}` : '';
    const passagesEtape: Etape = rang >= ordre.indexOf('prestation_realisee')
        ? { cle: 'passages', titre: 'Passages', detail: passages && passages.total > 1 ? `${passages.fait} sur ${passages.total}` : 'Réalisés', etat: 'fait' }
        : s === 'rdv_reserve'
            ? { cle: 'passages', titre: 'Passages', detail: `À réaliser${nbPassages}`, etat: 'en_cours' }
            : s === 'devis_signe'
                ? { cle: 'passages', titre: 'Passages', detail: 'Rendez-vous à réserver', etat: 'en_cours' }
                : { cle: 'passages', titre: 'Passages', detail: '—', etat: 'a_venir' };

    const facturation: Etape = s === 'cloturee'
        ? { cle: 'facturation', titre: 'Facturation', detail: 'Clôturé', etat: 'fait' }
        : s === 'prestation_realisee'
            ? { cle: 'facturation', titre: 'Facturation', detail: 'À clôturer', etat: 'en_cours' }
            : { cle: 'facturation', titre: 'Facturation', detail: '—', etat: 'a_venir' };

    return [demande, devis, passagesEtape, facturation];
}

// Façons dont un client peut accepter sans signer en ligne : le choix est écrit dans l'historique du dossier.
const MODES_ACCORD: [string, string][] = [
    ['telephone', 'par téléphone'], ['email', 'par e-mail'], ['en_personne', 'en personne'], ['papier', 'sur papier'],
];

export type ProchaineAction = { titre: string; aide: string; bouton?: string; action?: 'composer' | 'relancer' | 'defiler'; secondaire?: { texte: string; action: 'accepte' } };

/** La seule chose à faire maintenant, selon l'étape. Fonction pure (testable à la main, sans réseau). */
export function prochaineAction(d: any): ProchaineAction {
    const s = String(d?.stage || '');
    switch (s) {
        case 'demande_recue': return { titre: 'Demande à étudier', aide: "Lisez la demande (sélection, photos, estimation), puis préparez le devis.", bouton: 'Voir la demande', action: 'defiler' };
        case 'dossier_cree': return { titre: 'Devis à établir', aide: "Composez le devis avec le client : prestations, formule, adresse pour les frais kilométriques.", bouton: 'Établir le devis', action: 'composer', secondaire: { texte: 'Le client a déjà accepté', action: 'accepte' } };
        case 'devis_envoye': return d?.devis_signed_received_at
            ? { titre: 'Devis signé reçu', aide: "Le client a renvoyé son devis signé : vérifiez-le et validez l'étape.", bouton: 'Vérifier et valider', action: 'defiler' }
            : { titre: 'En attente de la réponse du client', aide: d?.devis_sent_at ? `Devis envoyé le ${jour(d.devis_sent_at)}. Sans réponse, vous pouvez le relancer.` : 'Le devis est parti. Sans réponse, vous pouvez le relancer.', bouton: 'Relancer le client', action: 'relancer', secondaire: { texte: 'Le client a accepté', action: 'accepte' } };
        case 'devis_signe': return { titre: 'Rendez-vous à réserver', aide: 'Le devis est accepté. Fixez le premier rendez-vous (ou envoyez le lien de réservation au client).', bouton: 'Réserver le rendez-vous', action: 'defiler' };
        case 'rdv_reserve': return { titre: 'Prestation à réaliser', aide: "Le rendez-vous est dans le planning. Il se termine depuis sa fiche, avec photos et signature du client.", bouton: 'Voir le suivi des passages', action: 'defiler' };
        case 'prestation_realisee': return { titre: 'À clôturer', aide: 'La prestation est réalisée. Vérifiez la facture et le paiement, puis clôturez le dossier.', bouton: 'Clôturer le dossier', action: 'defiler' };
        case 'cloturee': return { titre: 'Dossier terminé', aide: 'Rien à faire. Pensez à demander un avis au client si ce n\'est pas déjà fait.' };
        case 'perdue': return { titre: 'Dossier abandonné', aide: "Ce dossier est fermé. Vous pouvez le rouvrir depuis l'étape précédente si le client revient." };
        case 'annulee': return { titre: 'Dossier annulé', aide: 'Ce dossier est fermé.' };
        default: return { titre: 'Dossier', aide: '' };
    }
}

export function SuiviDossierResume({ dossier, dossierId, onRelancer, onAccepte, busy }: {
    dossier: any; dossierId?: string; onRelancer: () => void; onAccepte: (mode: string) => void; busy?: boolean;
}) {
    const [menuAccord, setMenuAccord] = React.useState(false);
    React.useEffect(() => {
        if (!menuAccord) return;
        const fermer = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuAccord(false); };
        window.addEventListener('keydown', fermer);
        return () => window.removeEventListener('keydown', fermer);
    }, [menuAccord]);
    const [passages, setPassages] = React.useState<{ fait: number; total: number } | null>(null);
    const stage = String(dossier?.stage || '');
    React.useEffect(() => {
        if (!dossierId || !['rdv_reserve', 'prestation_realisee', 'cloturee'].includes(stage)) { setPassages(null); return; }
        let annule = false;
        moduleApi.getPrestationPassagesAvancement(dossierId).then((r: any) => {
            if (annule || !r || r.error || !Array.isArray(r.passages)) return;
            setPassages({ total: r.passages.length, fait: r.passages.filter((p: any) => p?.avancement?.complet).length });
        }).catch(() => { /* la frise reste sans compteur */ });
        return () => { annule = true; };
    }, [dossierId, stage]);

    const etapes = etapesFrise(dossier, passages);
    const prochaine = prochaineAction(dossier);
    const ferme = stage === 'perdue' || stage === 'annulee';

    const agir = () => {
        // « Établir le devis » fait défiler jusqu'au panneau de l'étape : c'est lui qui propose le bon premier pas
        // (planning des passages si le client a une formule, sinon le composeur de devis).
        if (prochaine.action === 'relancer') onRelancer();
        else document.getElementById('panneau-etape')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="space-y-3">
            {!ferme && (
                <ol aria-label="Avancement du dossier" className="grid grid-cols-2 sm:grid-cols-4 rounded-2xl border border-border bg-surface overflow-hidden">
                    {etapes.map((e, i) => (
                        <li key={e.cle} aria-current={e.etat === 'en_cours' ? 'step' : undefined}
                            className={`px-4 py-3 flex items-center gap-3 min-w-0 ${i > 0 ? 'sm:border-l border-border' : ''} ${i > 1 ? 'border-t sm:border-t-0' : ''} ${i === 1 ? 'border-l sm:border-l' : ''}`}>
                            <span aria-hidden="true" className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold border-2 ${e.etat === 'fait' ? 'bg-green-600 border-green-600 text-white' : e.etat === 'en_cours' ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300' : 'border-border text-content-muted'}`}>
                                {e.etat === 'fait' ? <Check size={13} /> : i + 1}
                            </span>
                            <span className="min-w-0">
                                <span className="block text-xs font-bold text-content truncate">{e.titre}</span>
                                <span className="block text-[11px] text-content-muted truncate">{e.detail}<span className="sr-only"> ({e.etat === 'fait' ? 'terminé' : e.etat === 'en_cours' ? 'en cours' : 'à venir'})</span></span>
                            </span>
                        </li>
                    ))}
                </ol>
            )}
            <div className={`rounded-2xl border px-5 py-4 flex flex-wrap items-center justify-between gap-3 ${ferme || !prochaine.bouton ? 'bg-surface border-border' : 'bg-brand-50 dark:bg-brand-950/30 border-brand-200 dark:border-brand-900'}`}>
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-content-muted">{prochaine.bouton ? 'Prochaine action' : 'État'}</div>
                    <div className="text-base font-black text-content">{prochaine.titre}</div>
                    {prochaine.aide && <div className="text-xs text-content-muted mt-0.5 max-w-xl">{prochaine.aide}</div>}
                </div>
                {prochaine.bouton && (
                    <div className="flex flex-wrap items-center gap-2">
                        {prochaine.secondaire && (
                            <div className="relative">
                                <button type="button" disabled={busy} aria-haspopup="menu" aria-expanded={menuAccord} onClick={() => setMenuAccord((v) => !v)}
                                    className="px-3.5 py-2 rounded-full text-xs font-bold border border-border bg-surface text-content hover:border-content disabled:opacity-50">{prochaine.secondaire.texte} ▾</button>
                                {menuAccord && (
                                    <div role="menu" aria-label="Comment le client a-t-il accepté ?" className="absolute left-0 top-full mt-1.5 z-20 min-w-[210px] rounded-xl border border-border bg-surface shadow-xl p-1.5">
                                        <p className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-content-muted">Comment ?</p>
                                        {MODES_ACCORD.map(([cle, libelle]) => (
                                            <button key={cle} type="button" role="menuitem" onClick={() => { setMenuAccord(false); onAccepte(libelle); }}
                                                className="block w-full text-left px-2.5 py-2 rounded-lg text-xs font-semibold text-content hover:bg-surface-soft">{libelle}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <button type="button" disabled={busy} onClick={agir} className="px-5 py-2.5 rounded-full text-sm font-bold bg-brand-600 text-brand-fg hover:opacity-90 disabled:opacity-50">{prochaine.bouton}</button>
                    </div>
                )}
            </div>
        </div>
    );
}
